// @flow

import * as React from 'react';

import memoize from 'memoize-one';

import { selectCartCurrency, selectCartIsWorking } from '@gandi/cart';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Base, Text, TextPlaceholder, type BaseProps } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectCurrentBU } from 'src/modules/organization/selectors';

import styles from './Price.scss';

export type Props = {
  /**
   * price will add the currency
   * do not set price to show a loading state
   * set to 0 (zero) to show a "Free" label
   */
  price?: ?number,
  /**
   * priceCrossedOut will add the currency
   * useful to show a normal price when there is a catalog discount
   */
  priceCrossedOut?: ?number,
  hasDiscount?: boolean,
  showExcludeVatMention?: boolean,
  /**
   * Note that in order to allow the extraction of translations, priceInfo
   * can not be dynamically translated in this component.
   * You have to pass a translated string.
   */
  priceInfo?: string,
  priceInfoSize?: 'small' | 'base',
  ...$Exact<BaseProps>,
};

function showCrossedPrice(
  crossedPrice: mixed,
  price: mixed,
): implies crossedPrice is number {
  return (
    typeof crossedPrice === 'number' &&
    typeof price === 'number' &&
    crossedPrice > price
  );
}

// XXX: refactor? same as src/shared/Price.js
const getPriceInfo = memoize(
  (
    priceInfo: any,
    __: TranslatorProps['__'],
    currentBU: void | number,
    showExcludeVatMention: boolean = false,
  ) => {
    const pricesInfos = [];

    if (priceInfo) {
      pricesInfos.push(priceInfo);
    }

    if (showExcludeVatMention && currentBU !== 3) {
      pricesInfos.push(__('excl. VAT'));
    }

    return pricesInfos.join(' - ');
  },
);

export default function Price({
  price,
  priceCrossedOut,
  priceInfo,
  hasDiscount,
  showExcludeVatMention,
  priceInfoSize = 'small',
  ...baseProps
}: Props): React.MixedElement {
  const { __, formatPrice } = useTranslator();
  const currentBU = useAppSelector(selectCurrentBU);
  const currency = useAppSelector(selectCartCurrency);
  const isCartWorking = useAppSelector(selectCartIsWorking);

  const { color, ...restBaseProps } = baseProps;

  if (price === undefined || isCartWorking || Number.isNaN(price)) {
    return (
      <Base {...restBaseProps}>
        <TextPlaceholder label={__('Price unavailable')} />
      </Base>
    );
  }

  if (price === 0 && !hasDiscount) {
    return (
      <Base {...baseProps} className={styles.price}>
        {__('Free')}
      </Base>
    );
  }

  const priceInfos = getPriceInfo(
    priceInfo,
    __,
    currentBU,
    showExcludeVatMention,
  );

  return (
    <Base className={styles.root} {...baseProps}>
      {showCrossedPrice(Number(priceCrossedOut), Number(price)) && (
        <>
          <del className={styles.priceCrossedOut}>
            {formatPrice(Number(priceCrossedOut), currency)}
          </del>{' '}
        </>
      )}
      <span className={styles.price}>
        {formatPrice(Number(price), currency)}
      </span>
      {priceInfos && (
        <Text weight="regular" size={priceInfoSize} color={color ?? 'light'}>
          {priceInfos}
        </Text>
      )}
    </Base>
  );
}
